// @owners { team: patients }
import { useCallback } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
import getIsAuthorized from '~shared/selectors/auth/getIsAuthorized';
import { type ReduxDispatchShared } from '~shared/types';
import { type configureStore } from '~web/configureStore';

type WebReduxStore = ReturnType<typeof configureStore>['store'];

/**
 * Fetches initial data (deliveries).
 * Note: the redux store is passed into this hook because the redux provider
 * is rendered in PatientDashboardApp. This allows populating the redux state
 * and dispatching network request actions.
 *
 * Prescription pricing is no longer fetched here — it's owned by React Query
 * (useGetPrescriptionPricing) and mirrored into Redux by usePrescriptionPricingReduxBridge.
 * @returns
 */
export const useGetInitialData = ({ store }: { store: WebReduxStore }) => {
  const getInitialData = useCallback(async () => {
    const initialState = store.getState();
    const isAuthorized = getIsAuthorized(initialState);
    if (!isAuthorized) return;

    const dispatch = store.dispatch as ReduxDispatchShared;
    // eslint-disable-next-line import/no-deprecated
    await dispatch(fetchDeliveries());
  }, [store]);

  return { getInitialData };
};
